// SYN-2738 (I1) — turns an axios error from the API into a message a person can
// read, in the UI language. FastAPI sends `detail` in three shapes:
//   - a string                         → shown as is (legacy contract), EXCEPT on
//     5xx, where it is an internal error text → generic `server` sentence;
//   - an object `{ code|reason, message, …params }` → a known code/reason gets its
//     own i18n key (the backend `message` is pt-BR only); otherwise `message`;
//   - a 422 list `[{ loc, msg, type, ctx }]` → the password-policy rule when the
//     item says so, else a generic validation sentence. The raw list is NEVER
//     rendered (it used to reach the snackbar as JSON).
// Anything else falls back to `fallbackKey` (5xx → `server`).
//
// Converte o erro do axios numa mensagem legível no idioma da UI. `t` é injetado
// (useTranslation no componente, i18next.t no composable).
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_BYTES } from '@/composables/usePasswordPolicy';

const NS = 'utils.apiErrorMessage';

const plain = (key) => () => [key, {}];

// Null prototype: the code comes from the server — `"toString"` in a literal
// object would return the inherited function instead of `undefined`.
// Each entry returns `[key, params]`, or `null` when the payload lacks what the
// sentence needs (then the backend `message` / fallback is used instead).
const KNOWN_CODES = Object.assign(Object.create(null), {
    video_duration_over_plan_limit: (d) => (typeof d.max_video_duration_seconds === 'number'
        ? ['videoDurationOverPlanLimit', { limitMinutes: Math.round(d.max_video_duration_seconds / 60) }]
        : null),
    feature_not_available: plain('featureNotAvailable'),
    batch_limit_exceeded: (d) => (typeof d.max === 'number' ? ['batchLimitExceeded', { max: d.max }] : null),
    sso_required: plain('ssoRequired'),
    client_inactive: plain('clientInactive'),
    user_inactive: plain('userInactive'),
    sso_password_change_forbidden: plain('ssoPasswordChangeForbidden'),
    token_invalid_or_expired: plain('tokenInvalidOrExpired'),
    no_active_subscription: plain('noActiveSubscription'),
    // "costs X, you have Y" only when both numbers are there and something is
    // left; otherwise the balance is simply over.
    quota_exhausted: (d) => (typeof d.cost === 'number' && typeof d.remaining === 'number' && d.remaining > 0
        ? ['quotaInsufficient', { cost: d.cost, remaining: d.remaining }]
        : ['quotaExhausted', {}]),
    seat_limit_reached: plain('seatLimitReached'),
    topup_requires_active_plan: plain('topupRequiresActivePlan'),
    email_already_registered: plain('emailAlreadyRegistered'),
    // SYN-2762: trava de 1 assinatura principal viva por conta (409).
    main_subscription_exists: plain('mainSubscriptionExists'),
    // SYN-2763: autoatendimento só pede pacote; regras do Trocar de plano.
    self_service_addon_only: plain('selfServiceAddonOnly'),
    plan_change_source_not_live_main: plain('planChangeSourceNotLiveMain'),
    plan_change_invalid_plan: plain('planChangeInvalidPlan'),
    plan_change_same_plan: plain('planChangeSamePlan'),
    plan_change_currency_scheduled: plain('planChangeCurrencyScheduled'),
    plan_change_invalid_date: plain('planChangeInvalidDate'),
    plan_change_other_main_alive: plain('planChangeOtherMainAlive'),
    // SYN-2764: "Nova conta" (POST /admin/onboarding).
    onboarding_partner_forbidden: plain('onboardingPartnerForbidden'),
    onboarding_admin_requires_plan: plain('onboardingAdminRequiresPlan'),
    onboarding_system_plan: plain('onboardingSystemPlan'),
    onboarding_plan_not_found: plain('onboardingPlanNotFound'),
    // 503 do reenvio de convite quando o SMTP recusa/cai.
    invite_email_failed: plain('inviteEmailFailed'),
});

// Password policy rule (422 item `ctx.rule`) → sentence. Unknown rule → generic.
const PASSWORD_RULES = Object.assign(Object.create(null), {
    min_length: ['passwordPolicy.minLength', { min: PASSWORD_MIN_LENGTH }],
    max_bytes: ['passwordPolicy.maxBytes', { max: PASSWORD_MAX_BYTES }],
    letter: ['passwordPolicy.letter', {}],
    digit: ['passwordPolicy.number', {}],
    number: ['passwordPolicy.number', {}],
});

const nonEmpty = (v) => typeof v === 'string' && v.trim() !== '';

/** Known code/reason of an object detail → `[key, params]`, or null. */
export function knownDetail(detail) {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
    const entry = KNOWN_CODES[detail.code] ?? KNOWN_CODES[detail.reason];
    return entry ? entry(detail) : null;
}

/** The `code` (or legacy `reason`) of an object detail, or null. */
export function detailCode(error) {
    const detail = error?.response?.data?.detail;
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
    return detail.code ?? detail.reason ?? null;
}

/**
 * SYN-2738 (I3) — the 402 `quota_exhausted` detail as ONE sentence in the UI
 * language, without a call to action (the plan-limit dialogs add their own).
 * `cost` present = Brain Simulations (unified billing); absent = legacy
 * per-media franchise, named by `media_type`. Other details → null (the caller
 * keeps its legacy path).
 */
export function quotaSentence(detail, t) {
    if (!detail || typeof detail !== 'object' || detail.code !== 'quota_exhausted') return null;
    if (typeof detail.cost === 'number') {
        return typeof detail.remaining === 'number' && detail.remaining > 0
            ? t(`${NS}.quota.insufficient`, { cost: detail.cost, remaining: detail.remaining })
            : t(`${NS}.quota.exhaustedBrainSimulations`);
    }
    if (detail.media_type === 'image') return t(`${NS}.quota.exhaustedImage`);
    if (detail.media_type === 'video') return t(`${NS}.quota.exhaustedVideo`);
    return t(`${NS}.quota.exhausted`);
}

/** 422 list → the password-policy item as `[key, params]`, or null. */
function passwordPolicyItem(list) {
    const item = list.find((i) => i?.type === 'password_policy');
    if (!item) return null;
    return PASSWORD_RULES[item.ctx?.rule] ?? ['passwordPolicy.generic', {}];
}

/**
 * @param {unknown} error axios error (or anything thrown)
 * @param {string} fallbackKey full i18n key used when the payload has no usable text
 * @param {(key: string, params?: object) => string} t translate function
 * @param {object} [fallbackParams] i18n params of `fallbackKey`
 * @returns {string}
 */
export function apiErrorMessage(error, fallbackKey, t, fallbackParams) {
    const detail = error?.response?.data?.detail;
    const status = error?.response?.status;
    const known = knownDetail(detail);
    if (known) return t(`${NS}.${known[0]}`, known[1]);
    if (typeof status === 'number' && status >= 500) return t(`${NS}.server`);
    if (nonEmpty(detail)) return detail;
    if (Array.isArray(detail)) {
        const rule = passwordPolicyItem(detail);
        return rule ? t(`${NS}.${rule[0]}`, rule[1]) : t(`${NS}.validation`);
    }
    if (detail && typeof detail === 'object' && nonEmpty(detail.message)) return detail.message;
    return fallbackParams ? t(fallbackKey, fallbackParams) : t(fallbackKey);
}
